import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { Input } from '@/components/ui/Input';
import { HeroSection } from '@/components/common';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Button } from '@/components/ui/Button';
import { registerRequest, registerFailure } from '@/store/actions/authActions';
import { generateAllOtp } from '@/services/authApi';
import { AUTH_ROUTES } from '@/constants/routes/authRoutes';
import type { AppDispatch, RootState } from '@/store';
import type { RegisterFormValues, GenerateAllOtpResponse } from '@/types/auth.types';
import {
  doPasswordsMatch,
  isExactLength,
  isOnlyDigits,
  isRequired,
  isValidEmail,
  isValidPassword,
  minLength,
} from '@/utils/validation';

import './Register.css';
import { TEXT } from '@/constants/static/registerLabels';

const initialFormValues: RegisterFormValues = {
  fullName: '',
  email: '',
  mobileNumber: '',
  password: '',
  confirmPassword: '',
};

const extractTransactionIds = (
  data: GenerateAllOtpResponse['data']
): { emailTransactionId: string; mobileTransactionId: string } | null => {
  if (!data) return null;

  if (Array.isArray(data)) {
    const emailTx = data.find((item) => item.deliveryMethod === 'EMAIL');
    const mobileTx = data.find((item) => item.deliveryMethod === 'SMS');
    if (emailTx?.transactionId && mobileTx?.transactionId) {
      return {
        emailTransactionId: emailTx.transactionId,
        mobileTransactionId: mobileTx.transactionId,
      };
    }
    return null;
  }

  if (data.emailTransactionId && data.mobileTransactionId) {
    return {
      emailTransactionId: data.emailTransactionId,
      mobileTransactionId: data.mobileTransactionId,
    };
  }

  return null;
};

interface FormErrors {
  fullName?: string;
  email?: string;
  mobileNumber?: string;
  password?: string;
  confirmPassword?: string;
}

const Register = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isLoading, error, success } = useSelector((state: RootState) => state.auth);

  const [formValues, setFormValues] = useState<RegisterFormValues>(initialFormValues);
  const [touched, setTouched] = useState<Record<keyof RegisterFormValues, boolean>>({
    fullName: false,
    email: false,
    mobileNumber: false,
    password: false,
    confirmPassword: false,
  });
  const [isOtpLoading, setIsOtpLoading] = useState(false);

  const validateField = (
    name: keyof RegisterFormValues,
    value: string,
    allValues: RegisterFormValues
  ): string | undefined => {
    switch (name) {
      case 'fullName':
        if (!isRequired(value)) return 'Full Name is required';
        if (!minLength(value, 3)) return 'fullName must be at least 3 characters';
        return undefined;
      case 'email':
        if (!isRequired(value)) return 'Email address is required';
        if (!isValidEmail(value)) return 'Please enter a valid email address';
        return undefined;
      case 'mobileNumber':
        if (!isRequired(value)) return 'Mobile number is required';
        if (!isOnlyDigits(value)) return 'Mobile number must contain only digits';
        if (!isExactLength(value, 10)) return 'Mobile number must be exactly 10 digits';
        return undefined;
      case 'password':
        if (!isRequired(value)) return 'Password is required';
        if (!isValidPassword(value))
          return 'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
        return undefined;
      case 'confirmPassword':
        if (!isRequired(value)) return 'Please confirm your password';
        if (!doPasswordsMatch(allValues.password, value)) return 'Passwords do not match';
        return undefined;
      default:
        return undefined;
    }
  };

  const errors = useMemo<FormErrors>(() => {
    return {
      fullName: touched.fullName ? validateField('fullName', formValues.fullName, formValues) : undefined,
      email: touched.email ? validateField('email', formValues.email, formValues) : undefined,
      mobileNumber: touched.mobileNumber
        ? validateField('mobileNumber', formValues.mobileNumber, formValues)
        : undefined,
      password: touched.password ? validateField('password', formValues.password, formValues) : undefined,
      confirmPassword: touched.confirmPassword
        ? validateField('confirmPassword', formValues.confirmPassword, formValues)
        : undefined,
    };
  }, [formValues, touched]);

  const isFormValid = useMemo(() => {
    const fieldNames: (keyof RegisterFormValues)[] = Object.keys(formValues) as (keyof RegisterFormValues)[];
    return fieldNames.every((name) => !validateField(name, formValues[name], formValues));
  }, [formValues]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === 'mobileNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormValues((prev: RegisterFormValues) => ({ ...prev, [name]: digitsOnly }));
    } else {
      setFormValues((prev: RegisterFormValues) => ({ ...prev, [name]: value }));
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setTouched((prev: Record<keyof RegisterFormValues, boolean>) => ({ ...prev, [name]: true }));
  };

  const submitForm = async () => {
    setTouched({
      fullName: true,
      email: true,
      mobileNumber: true,
      password: true,
      confirmPassword: true,
    });

    if (!isFormValid) {
      // console.log('[Register] Form is invalid; not submitting');
      return;
    }

    const payload = {
      fullName: formValues.fullName.trim(),
      email: formValues.email.trim(),
      password: formValues.password,
      mobileNumber: formValues.mobileNumber,
    };

    // console.log('[Register] Dispatching registerRequest', payload);
    dispatch(registerRequest(payload));
  };

  useEffect(() => {
    if (!success) return;

    // console.log('[Register] Registration succeeded; initiating OTP generation');

    const initiateOtp = async () => {
      setIsOtpLoading(true);
      try {
        const response = await generateAllOtp({
          email: formValues.email.trim(),
          mobileNumber: formValues.mobileNumber,
          deliveryMethod: 'EMAIL',
        });

        // console.log('[Register] Generate-all OTP response:', response);

        const transactions = extractTransactionIds(response.data);
        if (!transactions) {
          // console.error('[Register] Generate-all response missing transaction IDs');
          dispatch(registerFailure('Invalid OTP response. Please try again.'));
          return;
        }

        // console.log('[Register] Navigating to /register/otp/verify');
        navigate(AUTH_ROUTES.REGISTER_VERIFY_OTP, {
          replace: true,
          state: {
            email: formValues.email.trim(),
            mobileNumber: formValues.mobileNumber,
            emailTransactionId: transactions.emailTransactionId,
            mobileTransactionId: transactions.mobileTransactionId,
          },
        });
      } catch (err) {
        // console.error('[Register] Generate-all OTP failed:', err);
        const message =
          err instanceof Error ? err.message : 'Failed to send OTP. Please try again.';
        dispatch(registerFailure(message));
      } finally {
        setIsOtpLoading(false);
      }
    };

    initiateOtp();
  }, [success, navigate, dispatch, formValues.email, formValues.mobileNumber]);

  const handleButtonSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // console.log('[Register] Form submit event triggered');
    submitForm();
  };

  const handleCreateAccountClick = () => {
    // console.log('[Register] Create Account button clicked');
    submitForm();
  };

  return (
    <div className="nova-register-page">
      <div className="nova-register-container">
        <HeroSection />

        {/* Right Form Section */}
        <div className="nova-form-section">
          <div className="nova-form-card">
            <div className="nova-form-header">
              <h2 className="nova-form-title">{TEXT.register.formTitle}</h2>
              <p className="nova-form-subtitle">
                {TEXT.register.formSubTitle}
              </p>
            </div>

            {error && (
              <Alert variant="danger" className="nova-alert" dismissible>
                {error}
              </Alert>
            )}

            <form onSubmit={handleButtonSubmit} noValidate>
              <Input
                id="fullName"
                name="fullName"
                label={TEXT.register.fullName.label}
                placeholder={TEXT.register.fullName.placeholder}
                value={formValues.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.fullName}
                autoComplete="fullName"
              />

              <Input
                id="email"
                name="email"
                type="email"
                label="Email Address"
                placeholder="Enter your email address"
                value={formValues.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.email}
                autoComplete="email"
              />

              <Input
                id="mobileNumber"
                name="mobileNumber"
                type="tel"
                label="Mobile Number"
                placeholder="Enter mobile number"
                value={formValues.mobileNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.mobileNumber}
                autoComplete="tel"
                inputMode="numeric"
                maxLength={10}
              />

              <PasswordInput
                id="password"
                name="password"
                label="Password"
                placeholder="Create password"
                value={formValues.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.password}
                autoComplete="new-password"
              />

              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm Password"
                placeholder="Confirm password"
                value={formValues.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.confirmPassword}
                autoComplete="new-password"
              />

              <div className="d-grid mt-4">
                <Button type="submit" isLoading={isLoading || isOtpLoading} disabled={!isFormValid || isOtpLoading} onClick={handleCreateAccountClick}>
                  {TEXT.register.createAccount}
                </Button>
              </div>
            </form>

            <div className="nova-form-footer">
              <p>
               {TEXT.register.loginText} 
               <span>  
                <Link to="/login" className="nova-login-link">
                  {TEXT.register.loginBtn}
                </Link>
              </span>

              
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Register;
